import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Plus, ChevronDown, ChevronRight, AlertTriangle, ChevronLeft } from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
// import AssetsSidebar from '../components/common/AssetsSidebar/AssetsSidebar';
import { fetchMapData, fetchMetadata, fetchLocationsFilter } from './Map/data/staticData';
import MaintenanceAuditPanel from './MaintenanceAuditPanel';
import './Dashboard.css';
import './Assets.css';
import './Maintenance.css';
import './styles/pages-common.css';
import { usePageLoader } from '../components/common/PageLoader/PageLoader';


// Helper to fetch locations for all sites in a state in parallel
const fetchAllLocationsForState = async (countryId, stateId, sites) => {
    try {
        const promises = sites.map(s => fetchLocationsFilter({
            country_id: countryId,
            state_id: stateId,
            site_id: s.site_id
        }));
        const results = await Promise.all(promises);
        const allLocs = results
            .filter(res => res && res.status && Array.isArray(res.data))
            .flatMap(res => res.data);
        return allLocs;
    } catch (e) {
        console.error('Error fetching all locations for state:', e);
        return [];
    }
};

// Helper to format telemetry timestamp into human-readable date & time (strictly from API)
const formatTelemetryTime = (rawTime) => {
    if (!rawTime) return '--';
    if (typeof rawTime !== 'string' && !(rawTime instanceof Date)) return String(rawTime);
    try {
        const normalized = typeof rawTime === 'string' && rawTime.includes(' ') && !rawTime.includes('T')
            ? rawTime.replace(' ', 'T')
            : rawTime;
        const date = new Date(normalized);
        if (isNaN(date.getTime())) return String(rawTime);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return String(rawTime);
    }
};

// Helper to hydrate poles with country and state
const hydratePolesWithCountryState = async (poles) => {
    if (!poles || poles.length === 0) return [];
    try {
        const { countries, sites, states } = await fetchMetadata();

        return poles.map(item => {
            let countryName = item.countryName || item.country || item.country_name || '';
            let stateName = item.stateName || item.state || item.state_name || '';

            if (!countryName || !stateName) {
                const matchedSite = sites.find(s => s.site_name === item.siteName);
                if (matchedSite) {
                    if (!countryName && matchedSite.site_country) {
                        const cObj = countries.find(c => String(c.country_id) === String(matchedSite.site_country));
                        if (cObj) countryName = cObj.country_name;
                    }
                    if (!stateName && matchedSite.site_state) {
                        const sObj = states.find(s => String(s.state_id) === String(matchedSite.site_state));
                        if (sObj) stateName = sObj.state_name;
                    }
                }
            }

            return {
                ...item,
                country: countryName || 'India',
                state: stateName || 'Delhi'
            };
        });
    } catch (error) {
        console.error("Error in hydrating poles:", error);
        return poles.map(item => ({
            ...item,
            country: item.country || 'India',
            state: item.state || 'Delhi'
        }));
    }
};

export const Maintenance = ({ setView }) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });
    const [alert, setAlert] = useState(null);
    const [activeTopNav, setActiveTopNav] = useState('Maintenance');

    const [polesList, setPolesList] = useState(() => {
        try {
            const stored = localStorage.getItem('cached_maintenance_poles');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });
    const { setPageLoading } = usePageLoader();

    useEffect(() => {
        setPageLoading(polesList === null);
        return () => setPageLoading(false);
    }, [polesList, setPageLoading]);
    const [selectedPoleId, setSelectedPoleId] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);
    const [expandedPoleTabs, setExpandedPoleTabs] = useState({});
    const [poleDetailsMap, setPoleDetailsMap] = useState({});

    const getPoleTab = (poleNumber) => expandedPoleTabs[poleNumber] || 'summary';
    const setPoleTab = (poleNumber, tab) => {
        setExpandedPoleTabs(prev => ({
            ...prev,
            [poleNumber]: tab
        }));
    };

    const [inletSpeed, setInletSpeed] = useState(0);
    const [outletSpeed, setOutletSpeed] = useState(0);
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        fanType: '',
        oldValue: 0,
        newValue: 0,
        renderId: ''
    });

    const handleConfirmFanSpeed = () => {
        if (confirmModal.fanType === 'Inlet') {
            setInletSpeed(confirmModal.newValue);
            updateFanSpeed(confirmModal.renderId, confirmModal.newValue, outletSpeed);
        } else if (confirmModal.fanType === 'Outlet') {
            setOutletSpeed(confirmModal.newValue);
            updateFanSpeed(confirmModal.renderId, inletSpeed, confirmModal.newValue);
        }
        setConfirmModal({ ...confirmModal, isOpen: false });
    };

    const handleCancelFanSpeed = () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
    };

    const handleRowClick = async (index, renderId) => {
        const isExpanded = expandedRow === index;
        if (isExpanded) {
            setExpandedRow(null);
        } else {
            setExpandedRow(index);
            try {
                const token = localStorage.getItem('accessToken');
                const authHeaders = {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json'
                };

                const [fanRes, poleRes] = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_BASE_URL}/show-fan-control-record/?device_id=${renderId}`, {
                        method: 'GET',
                        headers: authHeaders
                    }).catch(() => null),
                    fetch(`${process.env.REACT_APP_API_BASE_URL}/polewise-current-air-quality/?pole_id=${renderId}&_t=${Date.now()}`, {
                        method: 'GET',
                        headers: authHeaders
                    }).catch(() => null)
                ]);

                if (fanRes && fanRes.ok) {
                    const result = await fanRes.json();
                    if (result.status === 'success' && result.data) {
                        setInletSpeed(result.data.inlet_fan_speed || 0);
                        setOutletSpeed(result.data.outlet_fan_speed || 0);
                    } else {
                        setInletSpeed(0);
                        setOutletSpeed(0);
                    }
                } else {
                    setInletSpeed(0);
                    setOutletSpeed(0);
                }

                if (poleRes && poleRes.ok) {
                    const pResult = await poleRes.json();
                    if ((pResult.status || pResult.success) && pResult.data) {
                        const item = pResult.data;
                        const apiTs = item.DateStamp || item.date_stamp || item.last_telemetry || item.lastTelemetry || item.last_telemetry_received || item.received_datetime || item.received_utc || item.last_updated || item.lastUpdated || item.timestamp || item.Date || item.date || item.created_at || null;
                        if (apiTs) {
                            setPoleDetailsMap(prev => ({
                                ...prev,
                                [renderId]: {
                                    ...prev[renderId],
                                    ...item,
                                    lastTelemetry: apiTs,
                                    dailyVolumeCubicFeet: item.total_air_cleaned_cuft_last_24h ?? item.total_air_cleaned_so_far_today ?? item.dailyVolumeCubicFeet
                                }
                            }));
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching expanded pole data:', error);
                setInletSpeed(0);
                setOutletSpeed(0);
            }
        }
    };

    const updateFanSpeed = async (deviceId, inlet, outlet) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/fan-control/`, {
                method: 'POST',
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    device_id: deviceId,
                    inlet_speed: inlet,
                    outlet_speed: outlet,
                    user_role: user?.role || ''
                })
            });
            const result = await response.json();
            if (response.ok && result.status !== false) {
                setAlert({ type: 'success', message: 'Fan speed updated successfully.' });
                setTimeout(() => setAlert(null), 3000);
            } else {
                setAlert({ type: 'error', message: 'Failed to update fan speed.' });
                setTimeout(() => setAlert(null), 3000);
            }
        } catch (error) {
            console.error('Error updating fan speed:', error);
            setAlert({ type: 'error', message: 'Network error updating fan speed.' });
            setTimeout(() => setAlert(null), 3000);
        }
    };

    const fetchDashBoardData = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return null;

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/dashboard-counts/`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) throw new Error('Failed to fetch dashboard data');

            const result = await response.json();
            if (result.status && result.data) return result.data;
            throw new Error('Invalid dashboard response');
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            return null;
        }
    };

    const fetchUserDetails = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return null;
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error('Failed to fetch user details');
            const data = await response.json();
            if (data.status && data.data?.user) {
                const userData = data.data.user;
                if (userData.profile_picture && !userData.picture) userData.picture = userData.profile_picture;
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
                return userData;
            }
            throw new Error('Invalid user data');
        } catch {
            const stored = localStorage.getItem('user');
            if (stored) { const u = JSON.parse(stored); setUser(u); return u; }
            return null;
        }
    };

    // Dynamic API data lists for filters
    const [countriesList, setCountriesList] = useState([]);
    const [statesList, setStatesList] = useState([]);
    const [locationsList, setLocationsList] = useState([]);
    const [statusesList, setStatusesList] = useState([]);

    const uniqueCountries = React.useMemo(() => {
        return countriesList.map(c => c.country_name).filter(Boolean);
    }, [countriesList]);

    const uniqueStates = React.useMemo(() => {
        return statesList.map(s => s.state_name).filter(Boolean);
    }, [statesList]);

    const uniqueLocations = React.useMemo(() => {
        return locationsList.map(l => l.location_name).filter(Boolean);
    }, [locationsList]);

    const statusOptions = React.useMemo(() => {
        if (statusesList.length > 0) {
            return statusesList.map(s => s.description).filter(Boolean);
        }
        return ["Online", "Offline", "Awaiting Connection"];
    }, [statusesList]);

    // Load initial countries and status list
    useEffect(() => {
        const loadInitialData = async () => {
            const res = await fetchLocationsFilter();
            if (res && res.status) {
                setCountriesList(res.data || []);
                if (res.statusdata) {
                    setStatusesList(res.statusdata);
                }
            }
        };
        loadInitialData();
    }, []);

    const handleCountryChange = async (newCountry) => {
        setSelectedCountry(newCountry);
        setSelectedState('');
        setSelectedLocation('');
        setSelectedPoleId('');

        setStatesList([]);
        setLocationsList([]);

        if (!newCountry) return;

        const selectedC = countriesList.find(c => c.country_name === newCountry);
        if (selectedC) {
            const res = await fetchLocationsFilter({ country_id: selectedC.country_id });
            if (res && res.status) {
                setStatesList(res.data || []);
            }
        }
    };

    const handleStateChange = async (newState) => {
        setSelectedState(newState);
        setSelectedLocation('');
        setSelectedPoleId('');

        setLocationsList([]);

        if (!newState) return;

        const selectedC = countriesList.find(c => c.country_name === selectedCountry);
        const selectedS = statesList.find(s => s.state_name === newState);
        if (selectedC && selectedS) {
            // First fetch sites for this state
            const sitesRes = await fetchLocationsFilter({
                country_id: selectedC.country_id,
                state_id: selectedS.state_id
            });
            if (sitesRes && sitesRes.status && Array.isArray(sitesRes.data)) {
                // Then fetch locations for all sites in parallel
                const allLocs = await fetchAllLocationsForState(
                    selectedC.country_id,
                    selectedS.state_id,
                    sitesRes.data
                );
                setLocationsList(allLocs);
            }
        }
    };

    const filteredPolesForDropdown = React.useMemo(() => {
        let filtered = polesList || [];
        if (selectedCountry) {
            filtered = filtered.filter(p => p.country === selectedCountry);
        }
        if (selectedState) {
            filtered = filtered.filter(p => p.state === selectedState);
        }
        if (selectedLocation) {
            filtered = filtered.filter(p => p.locationName === selectedLocation);
        }
        if (selectedStatus) {
            filtered = filtered.filter(p => {
                const sStatus = String(p.poleStatus || '').trim().toLowerCase();
                const sStatusText = String(p.status_text || '').trim().toLowerCase();
                const fStatus = String(selectedStatus).trim().toLowerCase();
                return sStatus === fStatus || sStatusText === fStatus;
            });
        }
        return filtered;
    }, [polesList, selectedCountry, selectedState, selectedLocation, selectedStatus]);

    React.useEffect(() => {
        if (selectedPoleId) {
            const poleExists = filteredPolesForDropdown.some(p => p.poleNumber === selectedPoleId);
            if (!poleExists) setSelectedPoleId('');
        }
    }, [selectedPoleId, filteredPolesForDropdown]);

    const filteredPoles = (polesList || []).filter(p => {
        if (selectedCountry && p.country !== selectedCountry) return false;
        if (selectedState && p.state !== selectedState) return false;
        if (selectedPoleId && p.poleNumber !== selectedPoleId) return false;
        if (selectedLocation && p.locationName !== selectedLocation) return false;
        if (selectedStatus) {
            const sStatus = String(p.poleStatus || '').trim().toLowerCase();
            const sStatusText = String(p.status_text || '').trim().toLowerCase();
            const fStatus = String(selectedStatus).trim().toLowerCase();
            if (sStatus !== fStatus && sStatusText !== fStatus) return false;
        }
        return true;
    });

    useEffect(() => {
        let isMounted = true;

        const loadAllData = async () => {
            // Background check for user
            fetchUserDetails().then(userData => {
                if (!userData && isMounted) {
                    setAlert({ type: 'error', message: 'Error loading user information' });
                    setTimeout(() => setView && setView('signin'), 500);
                }
            });

            // Background fetch dashboard data (not blocking table)
            fetchDashBoardData();

            // Fetch map data
            try {
                const mapData = await fetchMapData();
                if (isMounted) {
                    if (mapData && mapData.length > 0) {
                        // Render poles list immediately with raw fields (default country/state)
                        const initialList = mapData.map(item => ({
                            ...item,
                            country: item.country || 'India',
                            state: item.state || 'Delhi'
                        }));
                        setPolesList(initialList);

                        // Hydrate country/state in the background
                        hydratePolesWithCountryState(mapData).then(hydrated => {
                            if (isMounted && hydrated) {
                                setPolesList(hydrated);
                                try {
                                    localStorage.setItem('cached_maintenance_poles', JSON.stringify(hydrated));
                                } catch (e) {
                                    console.warn('Failed to cache maintenance poles:', e);
                                }
                            }
                        });
                    } else {
                        setPolesList([]);
                    }
                }
            } catch (error) {
                console.error("Error loading map data in Maintenance:", error);
                if (isMounted) setPolesList([]);
            }
        };

        const refreshMapData = async () => {
            try {
                const mapData = await fetchMapData();
                if (mapData && mapData.length > 0) {
                    const hydratedData = await hydratePolesWithCountryState(mapData);
                    if (isMounted) {
                        setPolesList(hydratedData);
                        try {
                            localStorage.setItem('cached_maintenance_poles', JSON.stringify(hydratedData));
                        } catch (e) {
                            console.warn('Failed to cache maintenance poles:', e);
                        }
                    }
                }
            } catch (error) {
                console.error("Error refreshing map data:", error);
            }
        };

        loadAllData();
        const intervalId = setInterval(refreshMapData, 3 * 60 * 1000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [setView]);



    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="assets-root"
        >
            <InnerHeader
                user={user}
                setView={setView}
                onProfilePhotoSuccess={async () => {
                    setTimeout(async () => { await fetchUserDetails(); }, 500);
                    setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                }}
            />

            <div className="assets-secondary-bar">
                <div className="assets-title-wrap">
                    <span className="assets-page-title">Maintenance</span>
                </div>

                <div className="assets-top-nav">
                    {/* Navigation buttons commented out as requested - preserved for future use
                    {['Home', 'Dashboard', 'Analytics', 'Export Reports', 'Asset', 'Supplier', 'Pole Design', 'Maintenance']
                        .filter(label => {
                            if (label === 'Home') return true;
                            const role = user?.role;
                            const normRole = role ? role.replace(/[-_ ]/g, '').toLowerCase() : '';
                            if (label === 'Analytics' && normRole !== 'superadmin') return false;
                            if (normRole === 'technicalsupport') return true;
                            if (normRole === 'superadmin') return true;
                            if (normRole === 'government') return label === 'Dashboard';
                            if (normRole === 'maintenance' || normRole === 'research') {
                                return label === 'Dashboard' || label === 'Maintenance';
                            }
                            if (normRole === 'assetmanagement') {
                                return label !== 'Maintenance' && label !== 'Export Reports';
                            }
                            return true;
                        })
                        .map(label => (
                            <button
                                key={label}
                                className={`assets-top-nav-btn${activeTopNav === label ? ' active' : ''}`}
                                disabled={label === 'Export Reports'}
                                style={label === 'Export Reports' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                onClick={() => {
                                    setActiveTopNav(label);
                                    if (label === 'Home') setView && setView('home');
                                    else if (label === 'Dashboard') setView && setView('dashboard');
                                    else if (label === 'Analytics') setView && setView('analytics');
                                    else if (label === 'Asset') setView && setView('assets-list');
                                    else if (label === 'Supplier') setView && setView('supplier-list');
                                    else if (label === 'Pole Design') setView && setView('pole-designs');
                                    else if (label === 'Maintenance') setView && setView('maintenance');
                                    // 'Export Reports' not yet wired to a view in App.js
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    */}
                    <button
                        className="assets-top-nav-btn"
                        onClick={() => setView && setView('home')}
                        title="Back to Home"
                    >
                        ← Home
                    </button>
                </div>
            </div>

            <div className="assets-body">
                <div className="maintenance-container">
                    <div className="maintenance-controls">


                        <select
                            className="maintenance-select-pole"
                            value={selectedPoleId}
                            onChange={(e) => setSelectedPoleId(e.target.value)}
                        >
                            <option value="">Select Pole (All)</option>
                            {filteredPolesForDropdown.map((p, i) => (
                                <option key={i} value={p.poleNumber}>{p.poleNumber}</option>
                            ))}
                        </select>

                        <select
                            className="maintenance-select-pole"
                            value={selectedCountry}
                            onChange={(e) => handleCountryChange(e.target.value)}
                        >
                            <option value="">Select Country (All)</option>
                            {uniqueCountries.map((c, i) => (
                                <option key={i} value={c}>{c}</option>
                            ))}
                        </select>

                        <select
                            className="maintenance-select-pole"
                            value={selectedState}
                            onChange={(e) => handleStateChange(e.target.value)}
                        >
                            <option value="">Select State (All)</option>
                            {uniqueStates.map((s, i) => (
                                <option key={i} value={s}>{s}</option>
                            ))}
                        </select>

                        <select
                            className="maintenance-select-pole"
                            value={selectedLocation}
                            onChange={(e) => {
                                setSelectedLocation(e.target.value);
                                setSelectedPoleId('');
                            }}
                        >
                            <option value="">Select Location (All)</option>
                            {uniqueLocations.map((loc, i) => (
                                <option key={i} value={loc}>{loc}</option>
                            ))}
                        </select>

                        <select
                            className="maintenance-select-pole"
                            value={selectedStatus}
                            onChange={(e) => {
                                setSelectedStatus(e.target.value);
                                setSelectedPoleId('');
                            }}
                        >
                            <option value="">Select Status (All)</option>
                            {statusOptions.map((st, i) => (
                                <option key={i} value={st}>{st}</option>
                            ))}
                        </select>
                    </div>

                    <div className="maintenance-table-wrapper">
                        {alert && (
                            <div className={`assets-alert ${alert.type}`} style={{ marginBottom: 16 }}>
                                {alert.message}
                            </div>
                        )}
                        <div className="maintenance-table-header">
                            <div>Pole ID</div>
                            <div>Country</div>
                            <div>State</div>
                            <div>Location</div>
                            <div>Internal Temp.</div>
                            <div>Humidity %</div>
                            <div>Status</div>
                            {/* <div>Description</div> */}
                        </div>

                        {polesList === null ? (
                            <div className="skeleton-loader" style={{ padding: '20px' }}>
                                <div className="skeleton-bar" style={{ height: '30px', margin: '8px 0' }}></div>
                                <div className="skeleton-bar" style={{ height: '30px', margin: '8px 0' }}></div>
                                <div className="skeleton-bar" style={{ height: '30px', margin: '8px 0' }}></div>
                                <div className="skeleton-bar" style={{ height: '30px', margin: '8px 0' }}></div>
                                <div className="skeleton-bar" style={{ height: '30px', margin: '8px 0' }}></div>
                            </div>
                        ) : filteredPoles.length === 0 ? (
                            <div style={{ color: '#8a99ad', padding: '40px', textAlign: 'center', fontSize: '14px' }}>
                                No poles found matching criteria
                            </div>
                        ) : (
                            filteredPoles.map((pole, index) => {
                                const isExpanded = expandedRow === index;
                                const renderId = pole.poleNumber;

                                return (
                                    <React.Fragment key={index}>
                                        <div
                                            className={`maintenance-table-row ${isExpanded ? 'expanded' : ''}`}
                                            onClick={() => handleRowClick(index, renderId)}
                                        >
                                            <div className="m-pole-id">
                                                <span className="m-chevron">
                                                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                </span>
                                                <span className="m-text-id">{renderId}</span>
                                            </div>
                                            <div>{pole.country}</div>
                                            <div>{pole.state}</div>
                                            <div>{pole.locationName}</div>
                                            <div>
                                                {pole.temperature_c != null && pole.temperature_c !== '--' ? `${Math.round(pole.temperature_c)}°C` : (pole.aqiAfter?.temperature != null && pole.aqiAfter.temperature !== '--' ? `${Math.round(pole.aqiAfter.temperature)}°C` : '--')}
                                            </div>
                                            <div>
                                                {pole.humidity_percent != null && pole.humidity_percent !== '--' ? `${Math.round(pole.humidity_percent)}%` : (pole.aqiBefore?.humidity != null && pole.aqiBefore.humidity !== '--' ? `${Math.round(pole.aqiBefore.humidity)}%` : '--')}
                                            </div>
                                            {/* <div className={`status-badge ${pole.poleStatus === 'online' ? 'm-status-online' : 'm-status-offline'}`}>
                                                {pole.poleStatus ? pole.poleStatus.charAt(0).toUpperCase() + pole.poleStatus.slice(1) : 'Offline'}
                                            </div> */}
                                            <div>
                                                {/* {pole.poleStatusDescription} */}

                                                <span className={`status-badge bgcolor-${pole.poleStatusId}`}>
                                                    {pole.status_text}
                                                </span>
                                            </div>
                                        </div>
                                        {isExpanded && (
                                            <div className="maintenance-expanded-panel">
                                                {/* Main Navigation Tabs: Summary vs Audit */}
                                                <div className="m-panel-main-tabs">
                                                    <button
                                                        type="button"
                                                        className={`m-panel-main-tab-btn ${getPoleTab(renderId) === 'summary' ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPoleTab(renderId, 'summary');
                                                        }}
                                                    >
                                                        Summary
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`m-panel-main-tab-btn ${getPoleTab(renderId) === 'audit' ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPoleTab(renderId, 'audit');
                                                        }}
                                                    >
                                                        <span>Audit</span>
                                                        <ChevronDown size={14} />
                                                    </button>
                                                </div>

                                                {getPoleTab(renderId) === 'summary' ? (() => {
                                                    const detail = poleDetailsMap[renderId] || {};
                                                    const rawTelemetryTimestamp = detail.lastTelemetry || detail.DateStamp || detail.received_datetime || detail.last_updated || pole.lastTelemetry || pole.last_telemetry || pole.last_telemetry_received || pole.received_datetime || pole.DateStamp || pole.bucketStartUtc || pole.last_updated || pole.lastUpdated || pole.timestamp || pole.Date || pole.date;
                                                    const dailyVol = detail.dailyVolumeCubicFeet ?? pole.dailyVolumeCubicFeet ?? 0;

                                                    return (
                                                        <>
                                                            <div className="m-expanded-top">
                                                                <div className="m-temp-section">
                                                                    <span className="m-label-sm">Daily Volume (Cu Ft)</span>
                                                                    <div className="m-temp-val">{dailyVol}</div>
                                                                </div>
                                                                <div className="m-temp-section">
                                                                    <span className="m-label-sm">Last Telemetry Received</span>
                                                                    <div className="m-temp-val-text">{formatTelemetryTime(rawTelemetryTimestamp)}</div>
                                                                </div>
                                                                <div className="m-temp-section">
                                                                    <span className="m-label-sm">Telemetry warnings</span>
                                                                    <div className="m-temp-val">{pole.telemetryWarnings || pole.telemetry_warnings || 0}</div>
                                                                </div>
                                                            </div>
                                                            <div className="m-fans-section">
                                                                <div className="m-fan-card">
                                                                    <div className="m-fan-header">
                                                                        <span>Inlet Fan Speed</span>
                                                                    </div>
                                                                    <div className="m-label-sm">Speed Setting</div>
                                                                    <div className="m-slider-container">
                                                                        <input
                                                                            type="range"
                                                                            className="m-slider-input"
                                                                            min="0" max="100" step="20"
                                                                            value={inletSpeed}
                                                                            onChange={(e) => {
                                                                                const newValue = Number(e.target.value);
                                                                                if (newValue !== inletSpeed) {
                                                                                    setConfirmModal({
                                                                                        isOpen: true,
                                                                                        fanType: 'Inlet',
                                                                                        oldValue: inletSpeed,
                                                                                        newValue: newValue,
                                                                                        renderId: renderId
                                                                                    });
                                                                                }
                                                                            }}
                                                                            style={{ background: `linear-gradient(to right, #E99924 ${inletSpeed}%, #000 ${inletSpeed}%)` }}
                                                                        />
                                                                        <div className="m-slider-labels">
                                                                            {[0, 20, 40, 60, 80, 100].map(val => (
                                                                                <span key={'in-' + val} className={inletSpeed === val ? 'active' : ''}>{val}%</span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="m-fan-card">
                                                                    <div className="m-fan-header">
                                                                        <span>Outlet Fan Speed</span>
                                                                    </div>
                                                                    <div className="m-label-sm">Speed Setting</div>
                                                                    <div className="m-slider-container">
                                                                        <input
                                                                            type="range"
                                                                            className="m-slider-input"
                                                                            min="0" max="100" step="20"
                                                                            value={outletSpeed}
                                                                            onChange={(e) => {
                                                                                const newValue = Number(e.target.value);
                                                                                if (newValue !== outletSpeed) {
                                                                                    setConfirmModal({
                                                                                        isOpen: true,
                                                                                        fanType: 'Outlet',
                                                                                        oldValue: outletSpeed,
                                                                                        newValue: newValue,
                                                                                        renderId: renderId
                                                                                    });
                                                                                }
                                                                            }}
                                                                            style={{ background: `linear-gradient(to right, #E99924 ${outletSpeed}%, #000 ${outletSpeed}%)` }}
                                                                        />
                                                                        <div className="m-slider-labels">
                                                                            {[0, 20, 40, 60, 80, 100].map(val => (
                                                                                <span key={'out-' + val} className={outletSpeed === val ? 'active' : ''}>{val}%</span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    );
                                                })() : (
                                                    <MaintenanceAuditPanel
                                                        pole={pole}
                                                        renderId={renderId}
                                                        onShowToast={(t) => {
                                                            setAlert(t);
                                                            setTimeout(() => setAlert(null), 3000);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}

                        <div className="maintenance-pagination">
                            <div className="m-page-btn"><ChevronLeft size={14} /></div>
                            <div className="m-page-btn active">1</div>
                            <div className="m-page-btn"><ChevronRight size={14} /></div>
                        </div>
                    </div>
                </div>
            </div>
            {confirmModal.isOpen && (
                <div className="maintenance-modal-overlay">
                    <div className="maintenance-modal-content">
                        <h3>Confirm Fan Speed</h3>
                        <p>
                            Are you sure you want to change the <strong>{confirmModal.fanType}</strong> fan speed from <strong>{confirmModal.oldValue}%</strong> to <strong>{confirmModal.newValue}%</strong>?
                        </p>
                        <div className="maintenance-modal-actions">
                            <button className="m-btn-cancel" onClick={handleCancelFanSpeed}>Cancel</button>
                            <button className="m-btn-confirm" onClick={handleConfirmFanSpeed}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ 4. FOOTER (shared component) ═══════════════════════════════════ */}
            <InnerFooter />

        </motion.div>
    );
};
export default Maintenance;
